// 空から作ったケースで約定日を1行ずつ手で入れなくてよいよう、契約の分割予定（installments）をまとめて作る。
// 遅延の区分（期日超過）と遅延金額・次回約定日はここで持たず、作った分割予定から caseFacts の既存の計算（deriveContracts）で導く
import { deriveContracts, jstDate } from "./caseFacts"
import type { Scenario, ScenarioContract, ScenarioInstallment } from "./types"

/** 入金・遅延の状態の決め方。どれを選んでも、基準日時より後の約定日の回は未払い（今後の請求予定）にする */
export type PlanStatus = "from" | "all_paid" | "all_overdue"

export type InstallmentPlan = {
  count: number
  amountMode: "total" | "monthly"
  amount: number
  /** 毎月の約定日（日にち）。その月に無い日（31日など）は月末にする */
  dueDay: number
  /** 初回の月（YYYY-MM） */
  firstMonth: string
  status: PlanStatus
  /** status が from のときの、遅延が始まる回 */
  overdueFrom: number
}

/** サーバーの検証（1契約の分割予定の件数・金額の上限）と揃える */
export const PLAN_LIMITS = { count: 60, amount: 100_000_000 } as const

/** よく使う約定日（日にち） */
export const DUE_DAY_CHOICES = [5, 6, 23, 27, 31] as const

export const PLAN_STATUS_LABELS: Record<PlanStatus, string> = {
  from: "遅延が始まる回を指定（それより前は入金済み）",
  all_paid: "基準日時より前の回はすべて入金済み",
  all_overdue: "基準日時より前の回はすべて遅延",
}

const MONTH = /^(\d{4})-(\d{2})$/

const isInteger = (value: number, min: number, max: number) =>
  Number.isInteger(value) && value >= min && value <= max

export function planErrors(plan: InstallmentPlan): Partial<Record<keyof InstallmentPlan, string>> {
  const errors: Partial<Record<keyof InstallmentPlan, string>> = {}
  if (!isInteger(plan.count, 1, PLAN_LIMITS.count))
    errors.count = `1〜${PLAN_LIMITS.count}回で入れてください。`
  const minAmount = plan.amountMode === "total" ? Math.max(plan.count, 1) : 1
  if (!isInteger(plan.amount, minAmount, PLAN_LIMITS.amount))
    errors.amount =
      plan.amountMode === "total"
        ? "1回1円以上になる総額（円）を入れてください。"
        : "1円以上の月額（円）を入れてください。"
  if (plan.amountMode === "monthly" && plan.amount * plan.count > PLAN_LIMITS.amount)
    errors.amount = "総額が1億円を超えます。"
  if (!isInteger(plan.dueDay, 1, 31)) errors.dueDay = "1〜31日で入れてください。"
  const month = plan.firstMonth.match(MONTH)
  if (!month || Number(month[2]) < 1 || Number(month[2]) > 12)
    errors.firstMonth = "初回の月を入れてください。"
  if (plan.status === "from" && !isInteger(plan.overdueFrom, 1, Math.max(plan.count, 1)))
    errors.overdueFrom = `1〜${plan.count}回目で入れてください。`
  return errors
}

const pad = (value: number) => String(value).padStart(2, "0")

const scheduledOnOf = (firstMonth: string, months: number, dueDay: number) => {
  const [, year, month] = firstMonth.match(MONTH) ?? []
  const index = Number(year) * 12 + Number(month) - 1 + months
  const y = Math.floor(index / 12)
  const m = (index % 12) + 1
  const lastDay = new Date(Date.UTC(y, m, 0)).getUTCDate()
  return `${y}-${pad(m)}-${pad(Math.min(dueDay, lastDay))}`
}

const amountsOf = (plan: InstallmentPlan) => {
  if (plan.amountMode === "monthly") return Array.from({ length: plan.count }, () => plan.amount)
  const base = Math.floor(plan.amount / plan.count)
  return Array.from({ length: plan.count }, (_, i) =>
    i === 0 ? plan.amount - base * (plan.count - 1) : base
  )
}

const paidOf = (plan: InstallmentPlan, numberOfPayment: number) => {
  if (plan.status === "all_paid") return true
  if (plan.status === "all_overdue") return false
  return numberOfPayment < plan.overdueFrom
}

export function buildContract(
  plan: InstallmentPlan,
  asOf: string,
  base?: Pick<ScenarioContract, "propertyName">
): ScenarioContract {
  const today = jstDate(asOf)
  const amounts = amountsOf(plan)
  const installments = amounts.map((amount, i): ScenarioInstallment => {
    const numberOfPayment = i + 1
    const scheduledOn = scheduledOnOf(plan.firstMonth, i, plan.dueDay)
    const paid = scheduledOn < today && paidOf(plan, numberOfPayment)
    return {
      numberOfPayment,
      scheduledOn,
      paymentTotalAmount: amount,
      paidAmount: paid ? amount : 0,
      lastPaidAt: paid ? `${scheduledOn}T10:00:00+09:00` : null,
    }
  })
  const unpaid = installments.filter((row) => row.paidAmount < row.paymentTotalAmount)
  return {
    propertyName: base?.propertyName ?? "架空の検証用契約",
    numberOfPayment: plan.count,
    totalPaymentAmount: amounts.reduce((sum, amount) => sum + amount, 0),
    remainingPaymentCount: unpaid.length,
    remainingBalance: unpaid.reduce((sum, row) => sum + row.paymentTotalAmount, 0),
    installments,
  }
}

/**
 * 遅延日数。smooth_api の DunningEvaluationPointInTimeRewinder#overdue_days と同じ数え方にする
 */
export function scenarioOverdueDays(scenario: Scenario): number {
  if (!scenario.contracts?.length) return scenario.overdueDays
  const starts = deriveContracts(scenario)
    .flatMap((contract) => contract.installments)
    .filter((row) => row.state === "overdue")
    .map((row) => row.transferOn || row.scheduledOn)
    .sort()
  if (!starts[0]) return 0
  const days = (Date.parse(jstDate(scenario.asOf)) - Date.parse(starts[0])) / 86_400_000
  return Math.max(Math.round(days), 0)
}

export function applyPlan(
  scenario: Scenario,
  index: number,
  plan: InstallmentPlan
): Pick<Scenario, "contracts" | "overdueDays"> {
  const contracts = [...(scenario.contracts ?? [])]
  contracts[index] = buildContract(plan, scenario.asOf, contracts[index])
  return { contracts, overdueDays: scenarioOverdueDays({ ...scenario, contracts }) }
}

const monthBefore = (asOf: string, months: number) => {
  const date = jstDate(asOf) || "2026-01-01"
  return scheduledOnOf(date.slice(0, 7), -months, 1).slice(0, 7)
}

export function initialPlan(
  contract: ScenarioContract | undefined,
  asOf: string,
  nextDueOn?: string
): InstallmentPlan {
  const rows = [...(contract?.installments ?? [])].sort((a, b) =>
    a.scheduledOn < b.scheduledOn ? -1 : a.scheduledOn > b.scheduledOn ? 1 : 0
  )
  const first = rows[0]
  const today = jstDate(asOf)
  // 分割予定に書かない回があるケースのため、行の数だけでなく契約の分割回数も見る
  const declared = contract?.numberOfPayment ?? 0
  const count = Math.min(Math.max(declared, rows.length) || 12, PLAN_LIMITS.count)
  const firstMonth = first?.scheduledOn.slice(0, 7) ?? monthBefore(asOf, 6)
  const dueDay = Number((first?.scheduledOn ?? nextDueOn ?? "").slice(8, 10)) || 27
  const amount = rows[1]?.paymentTotalAmount ?? first?.paymentTotalAmount ?? 30_000
  const overdueRow = rows.find(
    (row) => row.scheduledOn < today && row.paidAmount < row.paymentTotalAmount
  )
  const pastCount = Array.from({ length: count }, (_, i) =>
    scheduledOnOf(firstMonth, i, dueDay)
  ).filter((on) => on < today).length
  return {
    count,
    amountMode: "monthly",
    amount,
    dueDay: Number.isInteger(dueDay) && dueDay >= 1 && dueDay <= 31 ? dueDay : 27,
    firstMonth,
    status: "from",
    overdueFrom: overdueRow?.numberOfPayment ?? Math.max(pastCount, 1),
  }
}
